/**
 * CUMUL DES PAIEMENTS D'UN SINISTRE — règle canonique, point de vérité unique côté frontend.
 *
 * Reprise À L'IDENTIQUE de la règle SQL déployée
 * (migration_cumul_reglements_cheque_sans_annulation.sql, appliquée aux 4 fonctions d'état,
 * et SinistreRepository.sumCumulPaiementsByNums) :
 *
 *   - version ACTIVE ;
 *   - c'est un RÈGLEMENT et non un recours  → encaissement !== true ;
 *   - un CHÈQUE a été assigné               → numeroCheque non vide ;
 *   - état EN_ATTENTE_D_ENCAISSEMENT ou ENCAISSE
 *     (ordinaux : VALIDE=0, REJETE=1, EN_ATTENTE_D_ENCAISSEMENT=2, EN_ATTENTE=3,
 *      ENCAISSE=4, CHEQUE_ANNULE=5) ;
 *   - AUCUNE LIGNE D'ANNULATION de chèque (CHEQUE_ANNULE) active portant le MÊME numéro de
 *     chèque ET la MÊME banque sur le même sinistre.
 *     L'annulation TECHNIQUE, elle, bascule l'original en REJETE → déjà exclu par l'état.
 *
 * ⚠️ Un règlement simplement VALIDÉ (sans chèque assigné) n'entre donc PAS dans le cumul :
 * il n'a pas encore donné lieu à un paiement.
 */

/** États pour lesquels un chèque est réputé émis, donc le montant payé. */
const ETATS_PAYES = ["EN_ATTENTE_D_ENCAISSEMENT", "ENCAISSE"];

const estActif = (d) => d?.activeData !== false;
const estReglement = (d) => d?.encaissement !== true; // false/undefined = règlement ; true = recours
const aUnCheque = (d) => !!(d?.numeroCheque && String(d.numeroCheque).trim() !== "");
const txt = (v) => String(v ?? "").trim();

/** Ligne d'ANNULATION de chèque (elle neutralise le paiement d'origine). */
export const estAnnulationCheque = (d) => estActif(d) && d?.etatDecaissement === "CHEQUE_ANNULE";

/**
 * Même chèque ? Le SQL compare `ann.numero_cheque = d.numero_cheque AND ann.banque = d.banque`.
 * En SQL, une comparaison avec NULL ne matche JAMAIS : une annulation sans banque renseignée
 * n'annule donc rien. On reproduit exactement cette sémantique (d'où le test de non-vacuité),
 * pour que le frontend et les états SQL affichent le MÊME cumul.
 */
const memeCheque = (annulation, reglement) =>
    txt(annulation?.numeroCheque) !== "" &&
    txt(annulation?.numeroCheque) === txt(reglement?.numeroCheque) &&
    txt(annulation?.banque) !== "" &&
    txt(annulation?.banque) === txt(reglement?.banque);

/**
 * Ce décaissement compte-t-il dans le cumul des paiements ?
 *
 * @param {object} d             le décaissement à évaluer
 * @param {Array}  decaissements TOUS les décaissements du sinistre (pour chercher une annulation)
 */
export const estPaiementCompte = (d, decaissements = []) => {
    if (!estActif(d) || !estReglement(d) || !aUnCheque(d)) return false;
    if (!ETATS_PAYES.includes(d?.etatDecaissement)) return false;
    const annule = (decaissements || []).some((ann) => estAnnulationCheque(ann) && memeCheque(ann, d));
    return !annule;
};

/**
 * Somme des paiements réellement effectués sur un sinistre.
 *
 * @param {Array}  decaissements       tous les décaissements du sinistre
 * @param {object} [options]
 * @param {string} [options.exclureCodeId] codeId d'un règlement à EXCLURE — typiquement le
 *        règlement EN COURS sur la fiche de demande : il n'est pas encore payé, il ne doit
 *        donc pas être compté dans le cumul des paiements ANTÉRIEURS.
 * @returns {number} le cumul
 */
export const cumulPaiementsSinistre = (decaissements = [], options = {}) => {
    const { exclureCodeId } = options;
    return (decaissements || [])
        .filter((d) => !exclureCodeId || d?.codeId !== exclureCodeId)
        .filter((d) => estPaiementCompte(d, decaissements))
        .reduce((somme, d) => somme + (parseFloat(d?.montantGlobal) || 0), 0);
};
